import {
  faAlien8bit,
  faChevronDown,
  faLink,
  faLock,
  faPlus,
} from "@fortawesome/pro-regular-svg-icons";
import { faCircleInfo, faPencil } from "@fortawesome/pro-solid-svg-icons";
import {
  Badge,
  Button,
  Dropdown,
  FormatDate,
  Grid,
  Icon,
  Inline,
  Input,
  Message,
  Modal,
  Section,
  TextArea,
  Tooltip,
} from "@intility/bifrost-react";
import Drawer from "@intility/bifrost-react/Drawer";
import Select from "@intility/bifrost-react-select";
import type { Columns } from "@intility/gjallarbru";
import { DataTable } from "@intility/gjallarbru";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  updateClientMembership,
  useClients,
  useUser,
} from "../../../api/clients";
import { useUpdatePlatform } from "../../../api/platforms";
import type {
  ClientListItem,
  PlatformDetail,
  UpdatePlatformRequest,
} from "../../../api/types";
import { ClientPurposeBadge } from "../../../components/ClientTypeBadge";
import { CopyButton } from "../../../components/CopyButton";
import { PlatformLogo } from "../../../components/platforms/PlatformLogo";
import type { LogoSelection } from "../../../components/platforms/PlatformLogoPicker";
import {
  logoSelectionToRequest,
  PlatformLogoPicker,
} from "../../../components/platforms/PlatformLogoPicker";
import {
  PlatformThemePicker,
  themeSwatchClass,
} from "../../../components/platforms/PlatformThemePicker";
import { SettingsField } from "../../../components/SettingsField";
import { clientPath } from "../../../lib/clientPath";
import { clientKindLabel } from "../../../lib/clientTypes";
import { builtinTheme, type ThemeChoice } from "../../../lib/platformIdentity";
import "./PlatformOverview.css";

function PlatformClients({
  platform,
  canEdit,
}: {
  platform: PlatformDetail;
  canEdit: boolean;
}) {
  const navigate = useNavigate();
  const { data: allClients = [] } = useClients();
  const queryClient = useQueryClient();

  const [showConnect, setShowConnect] = useState(false);
  const [existingClientId, setExistingClientId] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);

  const connectableClients = allClients.filter((c) => c.platformId === null);

  async function handleConnectExisting() {
    if (!existingClientId || !platform.id) return;
    setConnecting(true);
    setConnectError(null);
    try {
      await updateClientMembership(existingClientId, platform.id);
      await queryClient.invalidateQueries({
        queryKey: ["platforms", platform.id],
      });
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
      setExistingClientId("");
      setShowConnect(false);
    } catch (e) {
      setConnectError(e instanceof Error ? e.message : "Failed to connect.");
    } finally {
      setConnecting(false);
    }
  }

  const columns: Columns<ClientListItem> = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        meta: { primaryColumn: true },
      },
      {
        id: "type",
        accessorFn: (row: ClientListItem) =>
          clientKindLabel(row.appType, row.purposeTag),
        // The badge of the Clients list: the purpose, in the colour of the type.
        Cell: ({ row }: { row: { original: ClientListItem } }) => (
          <ClientPurposeBadge
            appType={row.original.appType}
            purposeTag={row.original.purposeTag}
          />
        ),
        header: "Kind",
      },
      {
        accessorKey: "purposeTag",
        header: "Purpose",
      },
      {
        accessorKey: "clientId",
        Cell: ({ row }: { row: { original: ClientListItem } }) => (
          <>
            <span>{row.original.clientId}</span>
            <span className="platform-client-id-copy">
              <CopyButton
                value={row.original.clientId}
                small={false}
                regularIcon
                style={{ color: "var(--bfc-base-c-2)" }}
              />
            </span>
          </>
        ),
        header: "Client ID",
      },
      {
        accessorKey: "createdAt",
        Cell: ({ row }: { row: { original: ClientListItem } }) =>
          row.original.createdAt ? (
            <FormatDate date={row.original.createdAt} />
          ) : (
            "—"
          ),
        header: "Created",
      },
    ],
    [],
  );

  return (
    <Section border radius background="base-2">
      <Section.Header style={{ backgroundColor: "var(--bfc-base-3)" }}>
        <Inline align="center">
          <Icon icon={faAlien8bit} />
          <Inline.Stretch>Clients</Inline.Stretch>
          {canEdit && (
            <Dropdown
              content={
                <>
                  <button
                    type="button"
                    onClick={() =>
                      navigate(`/clients/new?platformId=${platform.id}`)
                    }
                  >
                    <Dropdown.Item icon={faPlus}>
                      Create new client
                    </Dropdown.Item>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setConnectError(null);
                      setShowConnect(true);
                    }}
                  >
                    <Dropdown.Item icon={faLink}>
                      Connect existing client
                    </Dropdown.Item>
                  </button>
                </>
              }
            >
              <Button small variant="flat">
                <Icon icon={faPlus} style={{ marginRight: "8px" }} />
                New client
                <Icon icon={faChevronDown} style={{ marginLeft: "8px" }} />
              </Button>
            </Dropdown>
          )}
        </Inline>
      </Section.Header>

      <Modal
        isOpen={showConnect}
        onRequestClose={() => setShowConnect(false)}
        header="Connect an existing client"
        width={480}
      >
        <Grid gap={16}>
          <p className="bf-p" style={{ margin: 0 }}>
            Group a standalone client under <strong>{platform.name}</strong>.
            This only changes the grouping. The client keeps its own ownership.
          </p>
          {connectError && (
            <Message state="alert" header="Could not connect client">
              {connectError}
            </Message>
          )}
          <Select
            label="Client"
            hideLabel
            placeholder={
              connectableClients.length === 0
                ? "No standalone clients available"
                : "Select a client…"
            }
            options={connectableClients.map((c) => ({
              value: c.id,
              label: `${c.name} (${c.clientId})`,
            }))}
            value={
              connectableClients
                .map((c) => ({
                  value: c.id,
                  label: `${c.name} (${c.clientId})`,
                }))
                .find((o) => o.value === existingClientId) ?? null
            }
            onChange={(opt) =>
              setExistingClientId(
                (opt as { value: string } | null)?.value ?? "",
              )
            }
            isDisabled={connectableClients.length === 0}
          />
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <Button variant="flat" onClick={() => setShowConnect(false)}>
              Cancel
            </Button>
            <Button
              onClick={
                !existingClientId || connecting
                  ? undefined
                  : handleConnectExisting
              }
              state={!existingClientId || connecting ? "inactive" : undefined}
            >
              {connecting ? "Connecting…" : "Connect"}
            </Button>
          </div>
        </Grid>
      </Modal>

      {platform.clients.length === 0 ? (
        <Section.Content>
          <Message state="neutral">
            No clients yet. Add one to get started.
          </Message>
        </Section.Content>
      ) : (
        <DataTable
          border={false}
          columns={columns}
          data={platform.clients}
          name="platform-clients"
          tableOptions={{ enablePagination: false }}
          bifrostTableRowProps={{
            onClick: (
              event: React.MouseEvent | React.KeyboardEvent,
              row: { original: ClientListItem },
            ) => {
              // The copy button handles its own click.
              if (
                event.target instanceof Element &&
                event.target.closest("button, a")
              )
                return;
              navigate(clientPath(row.original));
            },
            style: { cursor: "pointer" },
          }}
          emptyState={{ title: "No clients yet." }}
        />
      )}
    </Section>
  );
}

const TECH_STACK_OPTIONS = [
  { value: "react", label: "React" },
  { value: "dotnet", label: ".NET" },
];

// `createdBy` holds the creator's attribution: a bare Keycloak user id for a
// person, "client:<clientId>" for a service caller. A person resolves to a
// display name through the shared user lookup; a client renders as it is and
// is never sent to the lookup. A missing or empty value renders nothing.
function useCreatorName(createdBy: string | null | undefined): string | null {
  const isClient = createdBy?.startsWith("client:") ?? false;
  const { data: user } = useUser(createdBy && !isClient ? createdBy : null);
  if (!createdBy) return null;
  if (isClient) return createdBy;
  return user?.displayName ?? createdBy;
}

function techStackLabel(value: string): string {
  return TECH_STACK_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

// The "Details" section: a read-only view of the platform's settings, with
// the logo and theme color in a row below the text fields. The header's Edit
// button swaps Name and Description into inputs, and the logo and color into
// pressable tiles that open their pickers in dropdowns. One Save sends every
// changed part through a single PATCH. Delete and the tech stack / blueprint
// editor (`PlatformEditDrawer`) live on the page header's buttons, so they are not
// triggered from here.
function PlatformSettings({
  platform,
  canEdit,
}: {
  platform: PlatformDetail;
  canEdit: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(platform.name);
  const [description, setDescription] = useState(platform.description ?? "");
  const [logoSelection, setLogoSelection] = useState<LogoSelection>({
    type: "none",
  });
  const [themeChoice, setThemeChoice] = useState<ThemeChoice>("neutral");
  const [logoPickerOpen, setLogoPickerOpen] = useState(false);
  const [themePickerOpen, setThemePickerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const updatePlatform = useUpdatePlatform(platform.id);
  const creatorName = useCreatorName(platform.createdBy);

  const saving = updatePlatform.isPending;
  // A builtin logo carries its theme: while one is selected, the theme
  // follows the logo and cannot be neutral.
  const forcedTheme =
    logoSelection.type === "builtin"
      ? builtinTheme(logoSelection.key)
      : undefined;
  const effectiveTheme: ThemeChoice = forcedTheme ?? themeChoice;
  const currentLogo = logoSelectionFromPlatform(platform.logo);
  const currentTheme: ThemeChoice = platform.theme ?? "neutral";
  const detailsDirty =
    name.trim() !== platform.name ||
    description !== (platform.description ?? "");
  const identityDirty =
    !logoSelectionEquals(logoSelection, currentLogo) ||
    effectiveTheme !== currentTheme;
  const dirty = detailsDirty || identityDirty;

  function startEditing() {
    // Re-seed from the platform each time so abandoned edits are discarded.
    setName(platform.name);
    setDescription(platform.description ?? "");
    setLogoSelection(logoSelectionFromPlatform(platform.logo));
    setThemeChoice(platform.theme ?? "neutral");
    setLogoPickerOpen(false);
    setThemePickerOpen(false);
    setError(null);
    setEditing(true);
  }

  // A choice in either picker closes its dropdown; the selection itself
  // stays pending until Save.
  function handleLogoChange(selection: LogoSelection) {
    setLogoSelection(selection);
    setLogoPickerOpen(false);
  }

  function handleThemeChange(theme: ThemeChoice) {
    setThemeChoice(theme);
    setThemePickerOpen(false);
  }

  async function saveDetails() {
    if (!name.trim()) return;
    setError(null);
    try {
      if (dirty) {
        const patch: UpdatePlatformRequest = {};
        if (detailsDirty) {
          patch.name = name.trim();
          patch.description = description;
        }
        if (!logoSelectionEquals(logoSelection, currentLogo)) {
          // Removing the logo must be sent explicitly: an absent logo field
          // means unchanged.
          patch.logo =
            logoSelection.type === "none"
              ? { type: "none" }
              : logoSelectionToRequest(logoSelection);
        }
        if (effectiveTheme !== currentTheme) {
          patch.theme = effectiveTheme;
        }
        await updatePlatform.mutateAsync(patch);
      }
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save changes.");
    }
  }

  return (
    <Section border radius background="base-2">
      <Section.Header style={{ backgroundColor: "var(--bfc-base-3)" }}>
        <Inline align="center">
          <Icon icon={faCircleInfo} />
          <Inline.Stretch>Details</Inline.Stretch>
          {editing ? (
            <>
              <Button
                small
                variant="flat"
                onClick={saving ? undefined : () => setEditing(false)}
                state={saving ? "inactive" : undefined}
              >
                Cancel
              </Button>
              <Button
                small
                variant="filled"
                onClick={saving ? undefined : saveDetails}
                state={
                  !dirty || saving || !name.trim() ? "inactive" : undefined
                }
              >
                {saving ? "Saving…" : "Save"}
              </Button>
            </>
          ) : (
            canEdit && (
              <Button small variant="flat" onClick={startEditing}>
                <Icon icon={faPencil} style={{ marginRight: 8 }} />
                Edit
              </Button>
            )
          )}
        </Inline>
      </Section.Header>
      <Section.Content>
        {error && (
          <Message state="alert" header="Could not save">
            {error}
          </Message>
        )}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr)",
            gap: 24,
            marginTop: error ? 16 : 0,
          }}
        >
          <SettingsField label="Name">
            {editing ? (
              <Input
                label="Name"
                hideLabel
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            ) : (
              <span className="bf-large">{platform.name}</span>
            )}
          </SettingsField>

          <SettingsField label="Description">
            {editing ? (
              <TextArea
                label="Description"
                hideLabel
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            ) : (
              <span
                className={
                  platform.description ? "bf-large" : "bf-large bfc-base-2"
                }
              >
                {platform.description || "—"}
              </span>
            )}
          </SettingsField>

          <SettingsField label="Tech stack">
            {platform.techStack.length > 0 ? (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {platform.techStack.map((v) => (
                  <Badge key={v} state="neutral" pill>
                    {techStackLabel(v)}
                  </Badge>
                ))}
              </div>
            ) : (
              <span className="bf-large bfc-base-2">—</span>
            )}
          </SettingsField>

          {/* The identity row: the logo and the theme color side by side,
              without the label-above-value structure of the fields above.
              In edit mode the same logo and dot sit in pressable tiles that
              open their pickers in dropdowns. */}
          <div className="platform-identity-row">
            {editing ? (
              <>
                <Dropdown
                  content={
                    <div style={{ width: 360 }}>
                      <PlatformLogoPicker
                        value={logoSelection}
                        onChange={handleLogoChange}
                      />
                    </div>
                  }
                  visible={logoPickerOpen}
                  onHide={() => setLogoPickerOpen(false)}
                >
                  <button
                    type="button"
                    className="platform-identity-edit"
                    aria-label="Change logo"
                    onClick={() => setLogoPickerOpen(true)}
                  >
                    <PlatformLogo
                      logo={logoSelectionToLogo(logoSelection)}
                      size={32}
                    />
                  </button>
                </Dropdown>
                {forcedTheme ? (
                  <Tooltip content="The theme follows the service logo.">
                    <span
                      role="img"
                      className="platform-identity-edit platform-identity-locked"
                      aria-label="The theme follows the service logo."
                    >
                      <span className={themeSwatchClass(effectiveTheme)} />
                      <Icon icon={faLock} className="platform-identity-lock" />
                    </span>
                  </Tooltip>
                ) : (
                  <Dropdown
                    content={
                      <PlatformThemePicker
                        value={effectiveTheme}
                        onChange={handleThemeChange}
                      />
                    }
                    visible={themePickerOpen}
                    onHide={() => setThemePickerOpen(false)}
                  >
                    <button
                      type="button"
                      className="platform-identity-edit"
                      aria-label="Change theme"
                      onClick={() => setThemePickerOpen(true)}
                    >
                      <span className={themeSwatchClass(effectiveTheme)} />
                    </button>
                  </Dropdown>
                )}
              </>
            ) : (
              <>
                <PlatformLogo logo={platform.logo} size={32} />
                <span className={themeSwatchClass(currentTheme)} />
              </>
            )}
          </div>
        </div>

        {platform.createdAt && (
          <p
            className="bf-p bfc-base-2"
            style={{
              color: "var(--bfc-base-c-2)",
              fontStyle: "italic",
              margin: "24px 0 0",
              paddingTop: 16,
              borderTop: "1px solid var(--bfc-base-c-dimmed)",
            }}
          >
            Created <FormatDate date={platform.createdAt} noTooltip />
            {creatorName && <> by {creatorName}</>}
          </p>
        )}
      </Section.Content>
    </Section>
  );
}

// The current logo as a picker selection. A custom logo without data (a
// backend edge) reads as no logo.
function logoSelectionFromPlatform(
  logo: PlatformDetail["logo"],
): LogoSelection {
  if (logo?.type === "builtin" && logo.key) {
    return { type: "builtin", key: logo.key };
  }
  if (logo?.type === "custom" && logo.data) {
    return { type: "custom", data: logo.data };
  }
  return { type: "none" };
}

function logoSelectionEquals(a: LogoSelection, b: LogoSelection): boolean {
  if (a.type !== b.type) return false;
  if (a.type === "builtin" && b.type === "builtin") return a.key === b.key;
  if (a.type === "custom" && b.type === "custom") return a.data === b.data;
  return true;
}

// The logo tile's preview of the current selection, in PlatformLogo's shape.
function logoSelectionToLogo(selection: LogoSelection): PlatformDetail["logo"] {
  if (selection.type === "none") return null;
  if (selection.type === "builtin") {
    return { type: "builtin", key: selection.key, data: null };
  }
  return { type: "custom", key: null, data: selection.data };
}

// Not mounted yet: it was opened from the removed kebab menu and is waiting to
// be wired to a header button. Exported so it is not dead code in the meantime.
export function PlatformEditDrawer({
  platform,
  isOpen,
  onClose,
}: {
  platform: PlatformDetail;
  isOpen: boolean;
  onClose: () => void;
}) {
  return (
    <Drawer
      header={`Edit ${platform.name}`}
      isOpen={isOpen}
      onRequestClose={onClose}
    >
      {/* Mount the form only while open so its fields re-seed from the
          platform on each open (and abandoned edits are discarded on close). */}
      {isOpen && <PlatformEditForm platform={platform} onClose={onClose} />}
    </Drawer>
  );
}

// Single form for everything editable on a platform: name/description and tech
// stack. The slug is fixed at creation (ADR-0056) and the blueprint exists with
// the Platform (ADR-0059), so neither is edited here. One Save commits all
// changed sections together.
function PlatformEditForm({
  platform,
  onClose,
}: {
  platform: PlatformDetail;
  onClose: () => void;
}) {
  const updatePlatform = useUpdatePlatform(platform.id);

  const [name, setName] = useState(platform.name);
  const [description, setDescription] = useState(platform.description ?? "");
  const [techStack, setTechStack] = useState<string[]>(platform.techStack);
  const [error, setError] = useState<string | null>(null);

  const techStackValue = TECH_STACK_OPTIONS.filter((o) =>
    techStack.includes(o.value),
  );

  const detailsDirty =
    name.trim() !== platform.name ||
    description !== (platform.description ?? "");
  const techStackDirty =
    techStack.length !== platform.techStack.length ||
    techStack.some((t) => !platform.techStack.includes(t));
  const dirty = detailsDirty || techStackDirty;

  const saving = updatePlatform.isPending;

  async function save() {
    if (!name.trim()) return;
    setError(null);
    try {
      const patch: UpdatePlatformRequest = {};
      if (detailsDirty) {
        patch.name = name.trim();
        patch.description = description;
      }
      if (techStackDirty) {
        // An empty array clears the stack. The backend ignores a null
        // (absent field), so null must never be sent for "clear".
        patch.techStack = techStack;
      }

      if (Object.keys(patch).length > 0) {
        await updatePlatform.mutateAsync(patch);
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save changes.");
    }
  }

  return (
    <Grid gap={20} style={{ padding: "8px 20px 24px" }}>
      <Input
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <TextArea
        label="Description"
        optional
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={3}
      />
      <Select
        label="Tech stack"
        placeholder="Not set"
        options={TECH_STACK_OPTIONS}
        value={techStackValue}
        onChange={(opts) => {
          const values =
            (opts as unknown as { value: string }[])?.map((o) => o.value) ?? [];
          setTechStack(values);
        }}
        isMulti
      />

      <div>
        <span
          className="bf-label"
          style={{ display: "block", marginBottom: 8 }}
        >
          Blueprint
        </span>
        <p className="bf-p">
          {platform.slug ? (
            <>
              <code>{platform.slug}</code>. The slug is fixed and cannot change.
            </>
          ) : (
            "This platform has no slug, so it has no blueprint."
          )}
        </p>
      </div>

      {error && <Message state="alert">{error}</Message>}

      <div style={{ display: "flex", gap: 8 }}>
        <Button
          onClick={saving ? undefined : save}
          state={!dirty || saving || !name.trim() ? "inactive" : undefined}
        >
          {saving ? "Saving…" : "Save changes"}
        </Button>
        <Button
          variant="flat"
          onClick={saving ? undefined : onClose}
          state={saving ? "inactive" : undefined}
        >
          Cancel
        </Button>
      </div>
    </Grid>
  );
}

// The default landing tab: a "Details" section (name, description, tech
// stack, logo, theme, creation date) beside a "Clients" section. Both live in
// this file; this only composes them.
export function PlatformOverview({
  platform,
  canEdit,
}: {
  platform: PlatformDetail;
  canEdit: boolean;
}) {
  return (
    <Grid gap={24}>
      <div>
        <h2 className="bf-h2" style={{ margin: 0 }}>
          Overview
        </h2>
        <p className="bf-large bfc-base-2" style={{ margin: "4px 0 0" }}>
          Overview of the platform
        </p>
      </div>
      {/* Details (375px) sits left of Clients; see PlatformOverview.css for how
          the columns wrap and Details widens to full width once they stack. */}
      <div className="platform-overview-columns">
        <div className="platform-overview-details">
          <PlatformSettings platform={platform} canEdit={canEdit} />
        </div>
        <div className="platform-overview-clients">
          <PlatformClients platform={platform} canEdit={canEdit} />
        </div>
      </div>
    </Grid>
  );
}
